import { Box, Grid, Paper, Typography, TextField, Switch, FormControlLabel, MenuItem, InputAdornment, Chip } from '@mui/material';
import { Memory, Speed } from '@mui/icons-material';
import { FilterConfig, K8sAnalyzerNodesListFilterPanelProps } from '../k8s-analyzer-types';

export function K8sAnalyzerNodesListFilterPanel({
    nodeFilter,
    podFilter,
    onNodeFilterChange,
    onPodFilterChange,
    totalNodes,
    filteredNodes
}: K8sAnalyzerNodesListFilterPanelProps) {
    const renderFilterGrid = (
        filter: FilterConfig,
        onFilterChange: (filter: FilterConfig) => void,
        title: string
    ) => (
        <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={filter.enabled}
                                onChange={(e) => onFilterChange({ ...filter, enabled: e.target.checked })}
                                color="primary"
                            />
                        }
                        label="Enable"
                    />
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={`Filter by ${title.toLowerCase()} Name`}
                            value={filter.nameFilter}
                            onChange={(e) => onFilterChange({ ...filter, nameFilter: e.target.value })}
                            disabled={!filter.enabled}
                            fullWidth
                            size="small"
                            placeholder={`Enter ${title.toLowerCase()} name...`}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="CPU Threshold %"
                            type="number"
                            value={filter.cpuThreshold}
                            onChange={(e) => onFilterChange({ ...filter, cpuThreshold: e.target.value })}
                            disabled={!filter.enabled}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Speed fontSize="small" color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Memory Threshold %"
                            type="number"
                            value={filter.memoryThreshold}
                            onChange={(e) => onFilterChange({ ...filter, memoryThreshold: e.target.value })}
                            disabled={!filter.enabled}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Memory fontSize="small" color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label="Memory Size"
                                type="number"
                                value={filter.memorySize}
                                onChange={(e) => onFilterChange({ ...filter, memorySize: e.target.value })}
                                disabled={!filter.enabled}
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Memory fontSize="small" color="primary" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                select
                                value={filter.memoryUnit}
                                onChange={(e) => onFilterChange({ 
                                    ...filter, 
                                    memoryUnit: e.target.value as 'Mi' | 'Gi' 
                                })}
                                disabled={!filter.enabled || !filter.memorySize}
                                size="small"
                                sx={{ width: '80px' }}
                            >
                                <MenuItem value="Mi">Mi</MenuItem>
                                <MenuItem value="Gi">Gi</MenuItem>
                            </TextField>
                            <TextField
                                select
                                value={filter.memoryCondition}
                                onChange={(e) => onFilterChange({ 
                                    ...filter, 
                                    memoryCondition: e.target.value as 'greaterThan' | 'lessThan'
                                })}
                                disabled={!filter.enabled || !filter.memorySize}
                                size="small"
                                sx={{ width: '140px' }}
                            >
                                <MenuItem value="greaterThan">&gt; Greater than</MenuItem>
                                <MenuItem value="lessThan">&lt; Less than</MenuItem>
                            </TextField>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );

    const renderFilterStatus = () => (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">
                    Filter Status:
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Using AND logic for all filters
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {nodeFilter.nameFilter && nodeFilter.enabled && (
                    <Chip 
                        size="small" 
                        label={`Node Name: ${nodeFilter.nameFilter}`}
                        color="primary"
                        onDelete={() => onNodeFilterChange({ ...nodeFilter, nameFilter: '' })}
                    />
                )}
                <Chip 
                    size="small" 
                    label={`Node CPU ≥ ${nodeFilter.cpuThreshold}%`} 
                    color={nodeFilter.enabled ? "primary" : "default"}
                    variant={nodeFilter.enabled ? "filled" : "outlined"}
                />
                <Chip 
                    size="small" 
                    label={`Node Memory ≥ ${nodeFilter.memoryThreshold}%`} 
                    color={nodeFilter.enabled ? "primary" : "default"}
                    variant={nodeFilter.enabled ? "filled" : "outlined"}
                />
                {podFilter.nameFilter && podFilter.enabled && (
                    <Chip 
                        size="small" 
                        label={`Pod Name: ${podFilter.nameFilter}`}
                        color="primary"
                        onDelete={() => onPodFilterChange({ ...podFilter, nameFilter: '' })}
                    />
                )}
                <Chip 
                    size="small" 
                    label={`Pod CPU ≥ ${podFilter.cpuThreshold}%`} 
                    color={podFilter.enabled ? "primary" : "default"}
                    variant={podFilter.enabled ? "filled" : "outlined"}
                />
                <Chip 
                    size="small" 
                    label={`Pod Memory ≥ ${podFilter.memoryThreshold}%`} 
                    color={podFilter.enabled ? "primary" : "default"}
                    variant={podFilter.enabled ? "filled" : "outlined"}
                />
            </Box>
            
            <Typography variant="body2" sx={{ mt: 2 }}>
                Showing {filteredNodes} of {totalNodes} nodes
            </Typography>
        </Paper>
    );

    return (
        <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
                {renderFilterGrid(nodeFilter, onNodeFilterChange, "Node")}
                {renderFilterGrid(podFilter, onPodFilterChange, "Pod")}
            </Grid>
            {renderFilterStatus()}
        </Box>
    );
} 